import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useSendingTransactionLookup } from '@/hooks/queries/useSpaceSending';
import { SendToSpaceContext } from '@/hooks/useSendToSpace';
import { SEND_PARAM, SEND_SPACE_PARAM, canSendTransaction } from '@/lib/spaces/sending';
import type { Transaction } from '@/types';

const SendToSpaceDrawer = lazy(() => import('./SendToSpaceDrawer'));

export function SendToSpaceProvider({ children }: { children: ReactNode }) {
  const { userId } = useAuth();
  return <SendingSession key={userId ?? 'signed-out'}>{children}</SendingSession>;
}

// What a row is in comes with the row (`Transaction.spaceExpenses`, VER-571): the session reads no index of the
// person's expenses, and the markers refresh with the transaction caches after a send or a removal.
function SendingSession({ children }: { children: ReactNode }) {
  const scope = useSpaceScope();
  const location = useLocation();
  const lookup = useSendingTransactionLookup();
  const [selected, setSelected] = useState<{ transaction: Transaction; location: string; spaceId?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const blocker = useBlocker(busy);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    blocker.reset();
    toast.info('Aguarde o envio terminar antes de sair.');
  }, [blocker]);
  const active = scope.enabled && scope.scope === 'personal';
  if (selected && (!active || (!busy && selected.location !== location.key))) setSelected(null);
  const open = (transaction: Transaction) => setSelected({ transaction, location: location.key });
  const openById = async (transactionId: string, spaceId?: string) => {
    try {
      const transaction = await lookup.trigger(transactionId);
      if (!canSendTransaction(transaction)) { toast.error('Só despesas com valor em reais podem entrar num espaço.'); return; }
      setSelected({ transaction, location: location.key, spaceId });
    } catch { toast.error('Não foi possível abrir este lançamento. Tente novamente.'); }
  };
  useSendDeepLink(active, openById);
  const close = () => setSelected(null);
  const transaction = active && selected && (busy || selected.location === location.key) ? selected.transaction : null;
  return <SendToSpaceContext.Provider value={active ? { open, openById, resolving: lookup.isMutating } : null}>
    {children}
    <Suspense fallback={null}>
      <OpenSendingDrawer transaction={transaction} initialSpaceId={selected?.spaceId} close={close} busy={busy} onBusy={setBusy} loading={scope.listLoading} error={scope.listError} retry={scope.retryList} />
    </Suspense>
  </SendToSpaceContext.Provider>;
}

// A `?send=<transaction>&space=<space>` link (a space's "Enviar") opens the drawer here. The params are dropped first,
// and the drawer opens on the address that follows: a selection is tied to the location it was made on.
function useSendDeepLink(active: boolean, openById: (transactionId: string, spaceId?: string) => Promise<void>) {
  const location = useLocation();
  const navigate = useNavigate();
  const requested = useRef<{ transactionId: string; spaceId?: string } | null>(null);
  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search);
    const transactionId = params.get(SEND_PARAM);
    if (transactionId) {
      requested.current = { transactionId, spaceId: params.get(SEND_SPACE_PARAM) ?? undefined };
      params.delete(SEND_PARAM);
      params.delete(SEND_SPACE_PARAM);
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
      return;
    }
    const request = requested.current;
    if (!request) return;
    requested.current = null;
    void openById(request.transactionId, request.spaceId);
  }, [active, location.key, location.pathname, location.search, navigate, openById]);
}

function OpenSendingDrawer({ transaction, ...props }: {
  transaction: Transaction | null;
  initialSpaceId?: string;
  close: () => void; busy: boolean; onBusy: (busy: boolean) => void;
  loading: boolean; error?: Error; retry: () => unknown;
}) {
  if (!transaction) return null;
  return <SendToSpaceDrawer key={`${transaction.id}:${props.initialSpaceId ?? ''}`} transaction={transaction} {...props} />;
}
